/**
 * Created by eladd on 12/20/2016.
 */

import {Injectable} from '@angular/core';
import {IMetadataGroup} from "../../../dev/components/elad.forms2/interfaces/metadata/imetadata.group";
import {IMetadata} from "../../../dev/components/elad.forms2/interfaces/metadata/imetadata";
import {PathEvaluator} from "../../../dev/components/elad.forms2/services/path.evaluator";
import {FULL_MODEL_PROP_NAME} from "../../clinical/metadata/services/metadata.service";
import * as _ from "lodash";
import * as path from 'path';
import { IModelLevel } from '../interfaces/imodellevel';

type Filter = { path?: string, type?: string };

@Injectable({ providedIn: 'root' })
  export class MetadataUtils {

	protected metadataCache = new Map<IMetadataGroup, { [path: string]: IMetadata }>();

	constructor(public pathEvaluator: PathEvaluator) {
	}

	getMetadata(modelMetadata: IMetadataGroup, pathInModel: string): IMetadata {
		let cached = this.metadataCache.get(modelMetadata);
		if (cached && cached[pathInModel]) {
			return cached[pathInModel];
		}

		if (pathInModel === FULL_MODEL_PROP_NAME) {
			cached = cached || {};
			cached[pathInModel] = {
				type: 'group',
				fields: modelMetadata
			};
			this.metadataCache.set(modelMetadata, cached);
			return cached[pathInModel];
		}

		let pathParts = _.toPath(pathInModel) as string[];

		//Special handle for a lookup column path
		let fkLookupRowsIndex = pathParts.indexOf("FKLookupRows");
		if (fkLookupRowsIndex > -1) {
			let lookupParts = pathParts.slice(fkLookupRowsIndex + 1);
			let joinText ='.lookupColumns.';

			//Add additional level for complex lookups
			let complexLookupTable = this.getComplexLookupTableName(pathParts, modelMetadata);
			if (complexLookupTable) {
				joinText += complexLookupTable + '.';
			}
			

			//Remove nested person FKLookuprows part, is it exists
			let nestedFkLookupRowsIndex = lookupParts.indexOf('FKLookupRows');
			if (nestedFkLookupRowsIndex > -1 && lookupParts[nestedFkLookupRowsIndex + 1] === 'personKey') {
				lookupParts.splice(nestedFkLookupRowsIndex, 2);
			}

			let lookupPath = lookupParts.join(joinText);
			pathParts.splice(fkLookupRowsIndex, pathParts.length - fkLookupRowsIndex, lookupPath);
			
		}

		let metadataPath = pathParts
			.filter(p => !this.isIndex(p))
			.join('.fields.');

		let metadata = <IMetadata> this.pathEvaluator.get(modelMetadata, metadataPath),
			lastProp = pathParts[pathParts.length - 1];

		if (!metadata) {
			throw new Error('No such path: ' + pathInModel);
		}

		cached = Object.assign(cached || {}, {
			[pathInModel]: this.isIndex(lastProp)
				? Object.assign({}, metadata, {multiple: false})
				: metadata
		});

		this.metadataCache.set(modelMetadata, cached);

		return cached[pathInModel];
	}

	getIdKey(metadata: IMetadataGroup): string {
		let overrideIdKey = ['spsItemKey', 'ppsItemKey'];
		for (let prop of overrideIdKey) {
			if (metadata.hasOwnProperty(prop) && metadata[prop].id) {
				return prop;
			}
		}
		
		for (let prop in metadata) {
			if (metadata.hasOwnProperty(prop)) {
				if (metadata[prop].id) {
					return prop;
				}
			}
		}
		throw new Error('No ID prop found in metadata!');
	}

	filter(metadata: IMetadataGroup, filter: Filter) {
		if (!filter.path && !filter.type) {
			return metadata;
		}

		if (filter.path) {
			filter.path = filter.path.toLowerCase();
		}

		return this._filter(metadata, filter, '');
	}

	flat(metadata: IMetadataGroup) {
		let res: Array<{
			metadata: IMetadata,
			path: string
		}> = [];

		this._flat(metadata, res);

		return res;
	}

	protected _flat(metadata: IMetadataGroup, res, relativePath = '') {
		for (let fieldName in metadata) {
			if (metadata.hasOwnProperty(fieldName)) {
				const field = metadata[fieldName];
				let path = relativePath ? `${relativePath}.${fieldName}` : fieldName;

				if (field.type === 'group') {
					if (field.multiple) {
						path = path + '.0';
					}

					this._flat(field.fields, res, path);
				} else {
					res.push({
						path,
						metadata: field
					});
				}
			}
		}
	}

	protected _filter(metadata: IMetadataGroup, filter: Filter, relativePath) {
		let res: IMetadataGroup = {};

		for (let field in metadata) {
			if (metadata.hasOwnProperty(field)) {
				let fieldLowerCase = field.toLowerCase();
				let fullPath = relativePath ? `${relativePath}.${fieldLowerCase}` : fieldLowerCase;

				if (!metadata[field].fields) {
					if (this.checkSimpleMetadata(metadata[field], fullPath, filter)) {
						res[field] = metadata[field];
					}
				} else {
					let filteredMetadata = this._filter(metadata[field].fields, filter, fullPath);
					if (Object.keys(filteredMetadata).length) {
						res[field] = Object.assign({}, metadata[field], {fields: filteredMetadata});
					}
				}
			}
		}

		return res;
	}

	protected checkSimpleMetadata(metadata: IMetadata, fullPath, {path, type}: Filter) {
		let typeCheck = !type || metadata.type === type;

		if (!path) {
			return typeCheck;
		} else if (!typeCheck) {
			return false;
		}

		let terms = path.split(' ').map(t => t.trim()).filter(t => t);

		for (let term of terms) {
			let pathCheck = fullPath.includes(term),
				labelCheck =
					metadata.defaults &&
					metadata.defaults.viewModel &&
					metadata.defaults.viewModel.label &&
					metadata.defaults.viewModel.label.toLowerCase().includes(term);

			if (!pathCheck && !labelCheck) {
				return false;
			}
		}

		return true;
	}

	isIndex(p: string) {
		return p === '$' || (+p).toString() === p;
	}


	private getComplexLookupTableName(pathParts: string[], modelMetadata: IMetadataGroup): string {
		let testParts = [...pathParts];

		let fkLookupRowsIndex = testParts.indexOf("FKLookupRows");
		let lookupParts = testParts.slice(fkLookupRowsIndex + 1);

		//Handle special case of person table in person resource
		let nestedFkLookupRowsIndex = lookupParts.indexOf('FKLookupRows');
		if (nestedFkLookupRowsIndex > -1 && lookupParts[nestedFkLookupRowsIndex + 1] === 'personKey') {
			return 'PERSON';
		}


		testParts.splice(fkLookupRowsIndex, testParts.length - fkLookupRowsIndex, lookupParts[0]);

		let metadataPath = testParts
			.filter(p => !this.isIndex(p))
			.join('.fields.');

		let metadata = <IMetadata> this.pathEvaluator.get(modelMetadata, metadataPath);

		if (metadata && metadata.type === 'complexLookup') {
			return metadata.referenceTable;
		}

		return null;

	}

	public getPatientModelLevelByFieldPath(path): IModelLevel {
		const normalizePath=this.pathEvaluator.normalize(path);
		
		for(const level of Object.values(IModelLevel)) {
			if(new RegExp(`(^|\\.)${level}(\\.|$)`).test(normalizePath)) {
				return level as IModelLevel;
			}
		}

		return IModelLevel.PATIENT;
	}
}
